
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ITournamentEditionRegisteredParticipant, IProfileWebsite, ITournamentEditionWebsite, OmneHostService, SanitizedResponse } from "../../../../services";
import { alertDialog, Classname, FileHelper, fileInputBuildFileInputPreview, FileInputPreviewType, Paginator, Scheme, StringHelper, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";
import { BaseI18nManager, i18nManager } from "../../../../i18n";

export interface ParticipantsProps {
    website: ITournamentEditionWebsite;
}

function Participants(props: ParticipantsProps) {

    const labels = i18nManager.Labels.gallery;
    const omnehostService = OmneHostService.getInstance();
    const [participants, setParticipants] = React.useState<ApiResponseData<ITournamentEditionRegisteredParticipant>>();
    const { page, setPage, country, setCountry, status, setStatus } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", country: "", status: "REGISTERED" } });

    React.useEffect(fetchInformation, [omnehostService, country, status]);

    return (
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>Tournament <em>Participants</em></h1>
            <p className={styles["sec-sub"]}>All registered participants — filter by country or status</p>

            <div className={styles["p-filters"]}>
                <select value={status} className={styles["p-select"]} onChange={e => {
                    setStatus(e.target.value);
                    //setPage("1");
                }} >
                    <option value="">All</option>
                    <option value="REGISTERED">Registered</option>
                    <option value="WITHDRAWN">Withdrawn</option>
                    <option value="REMOVED">Removed</option>
                </select>

                <select value={country} className={styles["p-select"]} onChange={e => {
                    //setPage("1");
                    setCountry(e.target.value);
                }}>
                    <option value="">🌍 All Countries</option>
                    {BaseI18nManager._countryCurrency.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
                </select>
            </div>

            {(!!participants?.content)
                ? ((participants.content && participants.content?.length > 0)
                    ? (<div className={styles["p-grid"]}>
                        {participants.content!.map((p) => {
                            const status = p.tournament_edition_registered.status;
                            const name = p.type === "TEAM" ? p.resource?.name : p.resource?.username;
                            const img = p.type === "TEAM" ? p.resource?.logo_url : p.resource?.profile_picture_url;
                            const banner = p.type === "TEAM" ? p.resource?.team_extra_data?.primary_banner_image : p.resource?.profile_banner_url;

                            return (
                                <ReactRouterDom.NavLink key={p.external_id} to={`${window.location.origin}/${p.type === "TEAM" ? "team" : "member"}/${p.resource?.external_id}`} target="_blank" className={Classname.build(styles["p-card"], styles["unlinked"])}>
                                    <img className={styles["p-banner"]} src={banner} alt={name} loading="lazy" />
                                    <img
                                        className={`${styles["p-avatar"]} ${p.type === "team" ? styles["sq"] : ""}`}
                                        src={img}
                                        alt={name}
                                        loading="lazy"
                                    />
                                    <div className={styles["p-body"]}>
                                        <span className={`${styles["p-type-badge"]} ${styles["badge-" + status?.toLowerCase?.()]}`}>
                                            {status === "REGISTERED" ? "Active" : StringHelper.toSaneSentenceFormat(status) ?? "UNKNOWN"}
                                        </span>
                                        <div className={styles["p-name"]}>{name}</div>
                                        <div className={styles["p-country"]}>{i18nManager.queryCountryCurrency(p.resource?.country_code ?? "NGA", "country_code", "country_name")}</div>

                                        <div className={styles["p-stats"]}>
                                            <div><div className={styles["p-stat-val"]}>{p.tournament_edition_registered?.own_matches_detail?.matches_won ?? 0}</div><div className={styles["p-stat-lbl"]}>Wins</div></div>
                                            <div><div className={styles["p-stat-val"]}>{p.tournament_edition_registered?.own_matches_detail?.matches_lost ?? 0}</div><div className={styles["p-stat-lbl"]}>Losses</div></div>
                                            <div><div className={styles["p-stat-val"]}>{p.tournament_edition_registered?.own_matches_detail?.matches_draw ?? 0}</div><div className={styles["p-stat-lbl"]}>Draws</div></div>
                                        </div>
                                    </div>
                                </ReactRouterDom.NavLink>
                            );
                        })}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}

            <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={participants?.size} totalRecords={participants?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                setPage("" + e.currentPage);
            }} template={{
                activePageLabel: () => "",
                nextPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                previousPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                lastPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                firstPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                pageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
            }} />
        </div>
    );

    function fetchInformation() {
        setParticipants({});
        omnehostService.queryTournamentEditionParticipants(props.website.tournament_edition.external_id, {
            page,
            status,
            sort: null,
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionRegisteredParticipant>) => {
            setParticipants(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Participants;